import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
const KEY = "healthion_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch (err) {
      console.error("Cart hydrate failed", err);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (err) {
      console.error("Cart persist failed", err);
    }
  }, [items]);

  const add = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.product_id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image_url: product.image_url,
          vendor_name: product.vendor_name,
          quantity: qty,
        },
      ];
    });
    toast.success(`${product.name} added to cart`);
    setOpen(true);
  }, []);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((p) => p.product_id !== id));
  }, []);

  const update = useCallback((id, qty) => {
    if (qty <= 0) {
      remove(id);
      return;
    }
    setItems((prev) => prev.map((p) => (p.product_id === id ? { ...p, quantity: qty } : p)));
  }, [remove]);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, add, update, remove, clear, subtotal, count, open, setOpen }),
    [items, add, update, remove, clear, subtotal, count, open]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
